import { Box, Button, Icon } from "@mui/material";
import { useTranslation } from "react-i18next";

import { CopyButton } from "~/components/Buttons";
import { downloadBlob } from "~/helpers/csvParser";
import logger from "~/helpers/logger";
import snackMessages from "~/helpers/snackMessages";
import { useSnacks } from "~/providers/SnackProvider";
import {
	buildRecoveryCodesFileContent,
	formatRecoveryCodesForCopy
} from "~settings/security/helpers/multiFactorRecoveryCodes";

const COPY_ACTION = "copying the recovery codes";

interface MultiFactorRecoveryCodesListProps {
	codes: readonly string[];
}

const MultiFactorRecoveryCodesList = ({ codes }: MultiFactorRecoveryCodesListProps) => {
	const { t } = useTranslation("common");
	const [openSnack] = useSnacks();

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(formatRecoveryCodesForCopy(codes));
			openSnack(t("actions.copy"), "success");
		} catch (err) {
			logger.error(snackMessages.actionError(COPY_ACTION), err);
			openSnack(snackMessages.actionError(COPY_ACTION), "error");
		}
	};

	const handleDownload = () => {
		downloadBlob(
			buildRecoveryCodesFileContent(codes),
			t("preferences.security.recoveryCodes.downloadFilename"),
			"text/plain;charset=utf-8;"
		);
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
			<Box
				component="ul"
				sx={{
					bgcolor: "action.hover",
					borderRadius: 1,
					columnGap: 2,
					columns: { sm: 2, xs: 1 },
					fontFamily: "monospace",
					listStyle: "none",
					m: 0,
					p: 2
				}}
			>
				{codes.map((code) => (
					<Box component="li" key={code}>
						{code}
					</Box>
				))}
			</Box>
			<Box sx={{ display: "flex", gap: 1 }}>
				<CopyButton onClick={handleCopy} />
				<Button onClick={handleDownload} startIcon={<Icon>download</Icon>} variant="outlined">
					{t("preferences.security.recoveryCodes.downloadButton")}
				</Button>
			</Box>
		</Box>
	);
};

export default MultiFactorRecoveryCodesList;
